import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, isVideoUrl } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Package, Image as ImageIcon, ShoppingBag, Settings as SettingsIcon, LogOut, Plus, Trash2, Edit3, Save, X, BarChart3, Tag, Upload, KeyRound, Layers, Menu as MenuIcon, Phone, Users } from "lucide-react";
import LiveVisitorsTab from "../components/LiveVisitorsTab";

const empty = (overrides = {}) => ({
  slug: "", name_id: "", name_en: "", description_id: "", description_en: "",
  price_idr: 0, price_usd: 0, pricing_mode: "unit", price_tiers: [], category: "decoration", stock: 100, weight_grams: 200,
  images: [""], variants: [], featured: false, ...overrides,
});

const slugify = (s = "") => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const TranslateBtn = ({ sourceText, direction = "id_to_en", onDone, testid }) => {
  const [busy, setBusy] = useState(false);
  const label = direction === "en_to_id" ? "Auto-translate dari Bahasa EN" : "Auto-translate dari Bahasa ID";
  const run = async () => {
    if (!sourceText?.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/translate", { text: sourceText, direction });
      onDone(data.translation);
    } catch (e) {
      alert(e?.response?.data?.detail || "Terjemahan gagal. Coba lagi.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || !sourceText?.trim()}
      data-testid={testid}
      className="text-[10px] text-holy-gold-dark hover:text-holy-red disabled:opacity-40 inline-flex items-center gap-1 mt-1 font-semibold"
      title="Isi otomatis dari bahasa lain menggunakan AI"
    >
      ⚡ {busy ? "Menerjemahkan…" : label}
    </button>
  );
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
    <input className="mt-1 w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:border-holy-red focus:ring-2 focus:ring-holy-red/10 outline-none" {...props} />
  </label>
);

const TextArea = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
    <textarea rows={3} className="mt-1 w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:border-holy-red focus:ring-2 focus:ring-holy-red/10 outline-none" {...props} />
  </label>
);

const Tab = ({ active, icon: Icon, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${active ? "bg-holy-red text-ivory" : "hover:bg-cream text-foreground"}`}>
    <Icon className="w-4 h-4" /> {children}
  </button>
);

// Reusable image uploader. Uploads to /api/upload and returns public URL.
const ImageUploader = ({ value, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = React.useRef(null);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store relative URL "/api/files/..." so it works in both preview and production
      // (browser resolves against current origin which routes /api to backend).
      onChange(data.url);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const isVid = isVideoUrl(value);

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 bg-cream rounded-xl flex items-center justify-center overflow-hidden border border-border shrink-0">
          {value ? (
            isVid ? (
              <video src={value} muted playsInline preload="metadata" onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }} className="w-full h-full object-cover" data-testid={`${testid}-video-preview`} />
            ) : (
              <img src={value} alt="" className="w-full h-full object-cover" />
            )
          ) : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <button type="button" onClick={pick} disabled={uploading} data-testid={testid} className="inline-flex items-center gap-2 bg-holy-red text-ivory rounded-full px-4 py-1.5 text-xs disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Upload Foto / Video"}
          </button>
          <div className="text-[10px] text-muted-foreground mt-1.5 break-all truncate">{value || "Belum ada media"}</div>
          <div className="text-[10px] text-muted-foreground">Foto max 5MB · Video (mp4/webm/mov) max 25MB</div>
          {err && <div className="text-xs text-holy-red mt-1">{err}</div>}
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { admin, logoutAdmin } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-holy-red">Admin Dashboard</h1>
          <p className="text-xs text-muted-foreground">{admin.email}</p>
        </div>
        <button onClick={() => { logoutAdmin(); navigate("/"); }} data-testid="admin-logout" className="text-sm flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:border-holy-red hover:text-holy-red">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
      <div className="grid md:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-1 bg-white rounded-2xl border border-border/60 p-3 h-fit">
          <Tab active={tab === "dashboard"} icon={BarChart3} onClick={() => setTab("dashboard")} testid="tab-dashboard">Dashboard</Tab>
          <Tab active={tab === "visitors"} icon={Users} onClick={() => setTab("visitors")} testid="tab-visitors">Live Visitors</Tab>
          <Tab active={tab === "products"} icon={Package} onClick={() => setTab("products")} testid="tab-products">Products</Tab>
          <Tab active={tab === "categories"} icon={Layers} onClick={() => setTab("categories")} testid="tab-categories">Categories</Tab>
          <Tab active={tab === "banners"} icon={ImageIcon} onClick={() => setTab("banners")} testid="tab-banners">Banners</Tab>
          <Tab active={tab === "menus"} icon={MenuIcon} onClick={() => setTab("menus")} testid="tab-menus">Menu Links</Tab>
          <Tab active={tab === "orders"} icon={ShoppingBag} onClick={() => setTab("orders")} testid="tab-orders">Orders</Tab>
          <Tab active={tab === "promos"} icon={Tag} onClick={() => setTab("promos")} testid="tab-promos">Promos</Tab>
          <Tab active={tab === "settings"} icon={SettingsIcon} onClick={() => setTab("settings")} testid="tab-settings">Settings</Tab>
          <Tab active={tab === "password"} icon={KeyRound} onClick={() => setTab("password")} testid="tab-password">Password</Tab>
        </aside>
        <main className="min-w-0">
          {tab === "dashboard" && <DashboardTab />}
          {tab === "visitors" && <LiveVisitorsTab />}
          {tab === "products" && <ProductsTab />}
          {tab === "categories" && <CategoriesTab />}
          {tab === "banners" && <BannersTab />}
          {tab === "menus" && <MenusTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "promos" && <PromosTab />}
          {tab === "settings" && <SettingsTab />}
          {tab === "password" && <PasswordTab />}
        </main>
      </div>
    </div>
  );
};

const DashboardTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div>Loading…</div>;
  const cards = [
    { label: "Total Products", value: stats.total_products, color: "text-holy-red" },
    { label: "Total Orders", value: stats.total_orders, color: "text-holy-green" },
    { label: "Paid Orders", value: stats.paid_orders, color: "text-holy-gold-dark" },
    { label: "Pending", value: stats.pending_orders, color: "text-foreground" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-border/60 p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
            <div className={`font-serif text-3xl mt-1 ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-border/60 p-6 mt-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revenue (Paid)</div>
        <div className="font-serif text-4xl text-holy-red mt-1">Rp{stats.revenue_idr.toLocaleString("id-ID")}</div>
      </div>
    </div>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const slugTouchedRef = React.useRef(false);

  const openEditor = (product = null) => {
    // Auto-slug stays active until the user manually edits the slug field in this editing session.
    // This way, if the admin corrects a typo in the product name (even on an existing product),
    // the slug updates automatically — unless they've already touched the slug themselves.
    slugTouchedRef.current = false;
    if (product) setEditing({ ...product, pricing_mode: product.pricing_mode || "unit", images: product.images?.length ? product.images : [""], variants: product.variants || [] });
    else setEditing(empty());
  };

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
    api.get("/categories/all").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  // Build hierarchical sorted list: parent first, then its children indented
  const catOptions = React.useMemo(() => {
    const parents = categories.filter((c) => !c.parent_slug).sort((a, b) => a.order - b.order);
    const out = [];
    for (const p of parents) {
      out.push({ ...p, depth: 0 });
      const kids = categories.filter((c) => c.parent_slug === p.slug).sort((a, b) => a.order - b.order);
      for (const k of kids) out.push({ ...k, depth: 1 });
    }
    return out;
  }, [categories]);

  const catLabel = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    return c ? `${c.icon || ""} ${c.name_id}` : slug;
  };

  const save = async () => {
    const payload = { ...editing, price_idr: +editing.price_idr, price_usd: +editing.price_usd, stock: +editing.stock, weight_grams: +editing.weight_grams, images: editing.images.filter(Boolean), price_tiers: (editing.price_tiers || []).filter(t => t.min_qty > 1).map(t => ({ min_qty: +t.min_qty, price_idr: +t.price_idr, price_usd: +t.price_usd })), variants: (editing.variants || []).filter(v => v.name).map(v => ({ name: v.name, color_hex: v.color_hex || "", image: v.image || "", stock: +v.stock || 0 })) };
    if (editing.id) {
      await api.put(`/products/${editing.id}`, payload);
    } else {
      await api.post("/products", payload);
    }
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl">Products</h2>
        <button data-testid="add-product-btn" onClick={() => openEditor(null)} className="inline-flex items-center gap-2 bg-holy-red text-ivory rounded-full px-5 py-2 text-sm">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-border/60 overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-cream text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Price IDR</th>
              <th className="text-left px-4 py-3">Stock</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-border/40">
                <td className="px-4 py-3 flex items-center gap-3">
                  {p.images?.[0] && <img src={p.images[0]} className="w-10 h-10 rounded object-cover bg-ivory" alt="" />}
                  <div><div className="font-medium">{p.name_en}</div><div className="text-xs text-muted-foreground">{p.slug}</div></div>
                </td>
                <td className="px-4 py-3 text-xs">{catLabel(p.category)}</td>
                <td className="px-4 py-3">Rp{p.price_idr.toLocaleString("id-ID")}</td>
                <td className="px-4 py-3">{p.stock}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEditor(p)} className="text-holy-red hover:underline mr-3" data-testid={`edit-${p.slug}`}><Edit3 className="inline w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-holy-red" data-testid={`delete-${p.slug}`}><Trash2 className="inline w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-ivory rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl text-holy-red">{editing.id ? "Edit" : "New"} Product</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Slug (otomatis dari nama)" value={editing.slug} onChange={(e) => { slugTouchedRef.current = true; setEditing({ ...editing, slug: e.target.value }); }} />
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Category</label>
                <select
                  data-testid="product-category-select"
                  value={editing.category || ""}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full bg-ivory border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-holy-red"
                >
                  <option value="">— Pilih Kategori —</option>
                  {catOptions.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.depth === 1 ? "\u00A0\u00A0\u00A0\u00A0↳ " : ""}
                      {c.icon} {c.name_id} ({c.slug})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Field label="Name (ID)" value={editing.name_id} onChange={(e) => {
                  const v = e.target.value;
                  setEditing({
                    ...editing,
                    name_id: v,
                    ...(!slugTouchedRef.current ? { slug: slugify(v) } : {}),
                  });
                }} />
                <TranslateBtn sourceText={editing.name_en} direction="en_to_id" onDone={(t2) => setEditing((prev) => ({ ...prev, name_id: t2, ...(!slugTouchedRef.current ? { slug: slugify(t2) } : {}) }))} testid="translate-name-id" />
              </div>
              <div>
                <Field label="Name (EN)" value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} />
                <TranslateBtn sourceText={editing.name_id} direction="id_to_en" onDone={(t2) => setEditing((prev) => ({ ...prev, name_en: t2 }))} testid="translate-name-en" />
              </div>
              <Field label="Price IDR" type="number" value={editing.price_idr} onChange={(e) => setEditing({ ...editing, price_idr: e.target.value })} />
              <Field label="Price USD" type="number" step="0.01" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: e.target.value })} />
              <div className="sm:col-span-2" data-testid="pricing-mode-toggle">
                <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Mode Harga</span>
                <div className="inline-flex bg-white border border-border rounded-full p-1 gap-1">
                  <button
                    type="button"
                    data-testid="pricing-mode-unit"
                    onClick={() => setEditing({ ...editing, pricing_mode: "unit" })}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${editing.pricing_mode !== "bundle" ? "bg-holy-red text-ivory" : "text-muted-foreground hover:text-holy-red"}`}
                  >
                    Satuan / Grosir per-pc
                  </button>
                  <button
                    type="button"
                    data-testid="pricing-mode-bundle"
                    onClick={() => setEditing({ ...editing, pricing_mode: "bundle" })}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${editing.pricing_mode === "bundle" ? "bg-holy-red text-ivory" : "text-muted-foreground hover:text-holy-red"}`}
                  >
                    📦 Paket Bundel
                  </button>
                </div>
              </div>
              <div className="sm:col-span-2 bg-cream rounded-2xl p-4 sm:p-5 border border-border" data-testid="price-tiers-editor">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                  <div>
                    <span className="font-serif text-base text-holy-red font-semibold">{editing.pricing_mode === "bundle" ? "📦 Paket Bundel" : "🏷️ Harga Grosir Bertingkat"}</span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">{editing.pricing_mode === "bundle" ? "(Wajib untuk mode paket)" : "(Opsional)"}</span>
                  </div>
                  <button
                    type="button"
                    data-testid="add-tier-btn"
                    onClick={() => setEditing({ ...editing, price_tiers: [...(editing.price_tiers || []), { min_qty: 3, price_idr: 0, price_usd: 0 }] })}
                    className="inline-flex items-center gap-1 bg-holy-red text-ivory text-xs px-3 py-1.5 rounded-full hover:bg-holy-red-dark transition-all"
                  >
                    <Plus className="w-3.5 h-3.5" /> {editing.pricing_mode === "bundle" ? "Tambah Paket" : "Tambah Tingkat"}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {editing.pricing_mode === "bundle"
                    ? "Customer bisa beli SATUAN (1 pc = harga Price IDR/USD di atas) ATAU per paket dengan harga TOTAL yang Anda buat di sini. Tambahkan paket sebanyak yang Anda mau (3, 6, 12, dst)."
                    : "Atur harga lebih murah jika customer membeli dalam jumlah banyak (misal: beli 3 pcs atau 6 pcs)."}
                </p>

                {/* Quick preset chips */}
                <div className="flex items-center gap-2 mb-3 text-xs">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Tambah Cepat:</span>
                  {[3, 6, 12].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        const exists = (editing.price_tiers || []).some((x) => +x.min_qty === n);
                        if (!exists) {
                          setEditing({ ...editing, price_tiers: [...(editing.price_tiers || []), { min_qty: n, price_idr: 0, price_usd: 0 }].sort((a,b) => a.min_qty - b.min_qty) });
                        }
                      }}
                      className="px-2.5 py-1 rounded-full bg-white border border-border hover:border-holy-red hover:text-holy-red transition-all font-medium"
                    >
                      {editing.pricing_mode === "bundle" ? `+ Paket ${n} pcs` : `+ Min ${n} pcs`}
                    </button>
                  ))}
                </div>

                {(editing.price_tiers || []).map((t, i) => (
                  <div key={i} className="bg-white rounded-xl p-3 border border-border/70 mb-2 shadow-sm" data-testid={`tier-edit-${i}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_1fr_auto] gap-2 items-center">
                      <div>
                        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{editing.pricing_mode === "bundle" ? "Isi Paket" : "Min. Qty"}</span>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="2"
                            value={t.min_qty}
                            onChange={(e) => setEditing({ ...editing, price_tiers: editing.price_tiers.map((x, j) => j === i ? { ...x, min_qty: e.target.value } : x) })}
                            className="w-full bg-ivory border border-border rounded-lg px-2.5 py-1.5 text-sm font-semibold text-center"
                            data-testid={`tier-minqty-${i}`}
                          />
                          <span className="text-xs text-muted-foreground">pcs</span>
                        </div>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{editing.pricing_mode === "bundle" ? "Harga Total Paket (IDR)" : "Harga/pc (IDR)"}</span>
                        <input
                          type="number"
                          min="0"
                          placeholder={editing.pricing_mode === "bundle" ? "mis. 400000" : "mis. 45000"}
                          value={t.price_idr || ""}
                          onChange={(e) => setEditing({ ...editing, price_tiers: editing.price_tiers.map((x, j) => j === i ? { ...x, price_idr: e.target.value } : x) })}
                          className="w-full bg-ivory border border-border rounded-lg px-3 py-1.5 text-sm"
                          data-testid={`tier-idr-${i}`}
                        />
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{editing.pricing_mode === "bundle" ? "Harga Total Paket (USD)" : "Harga/pc (USD)"}</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={editing.pricing_mode === "bundle" ? "mis. 25.00" : "mis. 3.00"}
                          value={t.price_usd || ""}
                          onChange={(e) => setEditing({ ...editing, price_tiers: editing.price_tiers.map((x, j) => j === i ? { ...x, price_usd: e.target.value } : x) })}
                          className="w-full bg-ivory border border-border rounded-lg px-3 py-1.5 text-sm"
                          data-testid={`tier-usd-${i}`}
                        />
                      </div>
                      <div className="flex justify-end sm:justify-center">
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, price_tiers: editing.price_tiers.filter((_, j) => j !== i) })}
                          className="p-1.5 text-muted-foreground hover:text-holy-red rounded-lg hover:bg-cream transition-colors"
                          title="Hapus tingkat ini"
                          data-testid={`tier-del-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {t.min_qty && t.price_idr > 0 && (
                      <div className="text-[11px] text-holy-green font-medium mt-1.5 pt-1.5 border-t border-border/40">
                        {editing.pricing_mode === "bundle"
                          ? `✓ Paket ${t.min_qty} pcs = Rp${Number(t.price_idr).toLocaleString("id-ID")} /paket ${t.price_usd > 0 ? `($${Number(t.price_usd).toFixed(2)})` : ""}`
                          : `✓ Beli ≥ ${t.min_qty} pcs → jadi Rp${Number(t.price_idr).toLocaleString("id-ID")} /pc ${t.price_usd > 0 ? `($${Number(t.price_usd).toFixed(2)})` : ""}`}
                      </div>
                    )}
                  </div>
                ))}
                {(editing.price_tiers || []).length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-2 bg-white/60 rounded-xl border border-dashed border-border">
                    Belum ada harga grosir. Klik <strong>+ Min 3 pcs</strong> atau <strong>+ Tambah Tingkat</strong> di atas untuk menambahkan.
                  </div>
                )}
              </div>
              <Field label="Stock" type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} />
              <Field label="Weight (g)" type="number" value={editing.weight_grams} onChange={(e) => setEditing({ ...editing, weight_grams: e.target.value })} />
              <div>
                <TextArea label="Description (ID)" value={editing.description_id} onChange={(e) => setEditing({ ...editing, description_id: e.target.value })} />
                <TranslateBtn sourceText={editing.description_en} direction="en_to_id" onDone={(t2) => setEditing((prev) => ({ ...prev, description_id: t2 }))} testid="translate-desc-id" />
              </div>
              <div>
                <TextArea label="Description (EN)" value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} />
                <TranslateBtn sourceText={editing.description_id} direction="id_to_en" onDone={(t2) => setEditing((prev) => ({ ...prev, description_en: t2 }))} testid="translate-desc-en" />
              </div>
              <div className="sm:col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Product Images (multiple)</span>
                <div className="mt-1 space-y-2">
                  {editing.images.map((img, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1">
                        <ImageUploader
                          value={img}
                          onChange={(url) => {
                            const next = [...editing.images];
                            next[i] = url;
                            setEditing({ ...editing, images: next });
                          }}
                          testid={`product-image-upload-${i}`}
                        />
                      </div>
                      {editing.images.length > 1 && (
                        <button type="button" onClick={() => setEditing({ ...editing, images: editing.images.filter((_, idx) => idx !== i) })} className="text-xs text-holy-red hover:underline">Remove</button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => setEditing({ ...editing, images: [...editing.images, ""] })} className="text-xs text-holy-red hover:underline" data-testid="add-product-image">+ Tambah Foto</button>
                </div>
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Variants / Colors</span>
                  <button type="button" onClick={() => setEditing({ ...editing, variants: [...(editing.variants || []), { name: "", color_hex: "#9B1C26", image: "", stock: 10 }] })} className="text-xs text-holy-red hover:underline" data-testid="add-variant">+ Tambah Warna</button>
                </div>
                <div className="space-y-2">
                  {(editing.variants || []).map((v, i) => (
                    <div key={i} className="bg-cream rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_100px_80px_auto] gap-2 items-center">
                      <input value={v.name} onChange={(e) => { const next = [...editing.variants]; next[i] = { ...v, name: e.target.value }; setEditing({ ...editing, variants: next }); }} placeholder="Warna (contoh: Merah)" className="bg-white border border-border rounded-lg px-3 py-2 text-sm" data-testid={`variant-name-${i}`} />
                      <input type="color" value={v.color_hex} onChange={(e) => { const next = [...editing.variants]; next[i] = { ...v, color_hex: e.target.value }; setEditing({ ...editing, variants: next }); }} className="h-9 w-full rounded-lg cursor-pointer" />
                      <input type="number" value={v.stock} onChange={(e) => { const next = [...editing.variants]; next[i] = { ...v, stock: +e.target.value }; setEditing({ ...editing, variants: next }); }} placeholder="Stock" className="bg-white border border-border rounded-lg px-2 py-2 text-sm w-full" />
                      <button type="button" onClick={() => setEditing({ ...editing, variants: editing.variants.filter((_, idx) => idx !== i) })} className="text-xs text-holy-red hover:underline">×</button>
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 mt-5">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-holy-red" />
                <span className="text-sm">Featured</span>
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button data-testid="save-product-btn" onClick={save} className="bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
              <button onClick={() => setEditing(null)} className="border border-border rounded-full px-6 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BannersTab = () => {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/banners/all").then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, order: +editing.order };
    if (editing.id) await api.put(`/banners/${editing.id}`, payload);
    else await api.post("/banners", payload);
    setEditing(null);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl">Banners</h2>
        <button data-testid="add-banner-btn" onClick={() => setEditing({ image_url: "", title_id: "", title_en: "", subtitle_id: "", subtitle_en: "", cta_label_id: "Belanja Sekarang", cta_label_en: "Shop Now", cta_link: "/shop", order: 0, active: true })} className="inline-flex items-center gap-2 bg-holy-red text-ivory rounded-full px-5 py-2 text-sm">
          <Plus className="w-4 h-4" /> Add Banner
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {banners.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-border/60 overflow-hidden">
            <div className="aspect-[16/9] bg-cream">{b.image_url && <img src={b.image_url} className="w-full h-full object-cover" alt="" />}</div>
            <div className="p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Order {b.order} · {b.active ? "Active" : "Hidden"}</div>
              <div className="font-serif text-lg mt-1">{b.title_en}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(b)} className="text-xs text-holy-red hover:underline" data-testid={`edit-banner-${b.id}`}>Edit</button>
                <button onClick={async () => { await api.delete(`/banners/${b.id}`); load(); }} className="text-xs text-muted-foreground hover:text-holy-red">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-ivory rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl text-holy-red">{editing.id ? "Edit" : "New"} Banner</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Banner Image</span>
                <div className="mt-1">
                  <ImageUploader
                    value={editing.image_url}
                    onChange={(url) => setEditing({ ...editing, image_url: url })}
                    testid="banner-image-upload"
                  />
                </div>
              </div>
              <Field label="Title (ID)" value={editing.title_id} onChange={(e) => setEditing({ ...editing, title_id: e.target.value })} />
              <Field label="Title (EN)" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} />
              <Field label="Subtitle (ID)" value={editing.subtitle_id} onChange={(e) => setEditing({ ...editing, subtitle_id: e.target.value })} />
              <Field label="Subtitle (EN)" value={editing.subtitle_en} onChange={(e) => setEditing({ ...editing, subtitle_en: e.target.value })} />
              <Field label="CTA Label (ID)" value={editing.cta_label_id} onChange={(e) => setEditing({ ...editing, cta_label_id: e.target.value })} />
              <Field label="CTA Label (EN)" value={editing.cta_label_en} onChange={(e) => setEditing({ ...editing, cta_label_en: e.target.value })} />
              <Field label="CTA Link" value={editing.cta_link} onChange={(e) => setEditing({ ...editing, cta_link: e.target.value })} />
              <Field label="Order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              <label className="flex items-center gap-2 mt-5">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-holy-red" />
                <span className="text-sm">Active</span>
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={save} className="bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
              <button onClick={() => setEditing(null)} className="border border-border rounded-full px-6 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [waybillEdit, setWaybillEdit] = useState({}); // {orderId: {waybill_id, courier_code}}
  const [detail, setDetail] = useState(null); // order object being viewed
  const reload = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { reload(); }, []);
  const update = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    reload();
  };
  const saveWaybill = async (id) => {
    const w = waybillEdit[id] || {};
    if (!w.waybill_id) return;
    await api.put(`/orders/${id}/waybill`, { waybill_id: w.waybill_id, courier_code: w.courier_code || "jne" });
    setWaybillEdit((s) => ({ ...s, [id]: undefined }));
    reload();
  };

  const getWaLink = (o) => {
    const phone = String(o.customer_phone || "").replace(/\D/g, "");
    if (!phone) return null;
    const waNum = phone.startsWith("0") ? `62${phone.slice(1)}` : phone;
    const firstName = (o.customer_name || "Kak").trim().split(" ")[0];
    const itemNames = (o.items || []).map((it) => it.name).join(", ");
    const isUnpaid = o.payment_status !== "paid";
    let msg = `Halo Kak ${firstName} 👋 Terima kasih sudah memilih hiasan Natal cantik di Holy Flowy! 🎄✨\n\n`;
    if (isUnpaid) {
      msg += `Kami melihat pesanan Kakak dengan nomor *${o.order_number}* senilai *Rp${(o.total_idr || 0).toLocaleString("id-ID")}* (${itemNames}) masih menunggu proses pembayaran.\n\nApakah ada kendala saat melakukan pembayaran atau ada pertanyaan terkait produk? Jika butuh bantuan pembayaran (transfer bank / QRIS), kami siap bantu ya Kak 😊\n\nStok produk handmade kami terbatas, jika ingin kami keep-kan mohon konfirmasi ya Kak. Terima kasih! ❤️`;
    } else {
      msg += `Pesanan Kakak dengan nomor *${o.order_number}* senilai *Rp${(o.total_idr || 0).toLocaleString("id-ID")}* (${itemNames}) sudah kami terima dan sedang kami proses dengan penuh cinta ❤️.\n\nKami akan segera kabari begitu paket siap dikirim. Terima kasih!`;
    }
    return `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
  };
  return (
    <div>
      <h2 className="font-serif text-2xl mb-4">Orders</h2>
      <div className="bg-white rounded-2xl border border-border/60 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-cream text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Waybill</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const we = waybillEdit[o.id] || {};
              return (
                <tr key={o.id} className="border-t border-border/40 align-top">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setDetail(o)}
                      data-testid={`order-detail-${o.order_number}`}
                      className="font-mono text-xs text-holy-red hover:underline text-left"
                      title="Lihat detail order"
                    >
                      {o.order_number} ↗
                    </button>
                  </td>
                  <td className="px-4 py-3">{o.customer_name}<div className="text-xs text-muted-foreground">{o.customer_email}</div></td>
                  <td className="px-4 py-3">Rp{o.total_idr.toLocaleString("id-ID")}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-holy-green/15 text-holy-green" : "bg-holy-gold/15 text-holy-gold-dark"}`}>{o.payment_status}</span></td>
                  <td className="px-4 py-3">
                    <select value={o.status} onChange={(e) => update(o.id, e.target.value)} className="text-xs bg-white border border-border rounded px-2 py-1">
                      {["pending", "processing", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 min-w-[220px]">
                    {o.waybill_id ? (
                      <div>
                        <div className="font-mono text-xs">{o.waybill_id}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">{o.shipping_courier_code}</div>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <input
                          placeholder="Waybill"
                          value={we.waybill_id || ""}
                          onChange={(e) => setWaybillEdit((s) => ({ ...s, [o.id]: { ...we, waybill_id: e.target.value } }))}
                          className="text-xs bg-white border border-border rounded px-2 py-1 w-20"
                        />
                        <select
                          value={we.courier_code || "jne"}
                          onChange={(e) => setWaybillEdit((s) => ({ ...s, [o.id]: { ...we, courier_code: e.target.value } }))}
                          className="text-xs bg-white border border-border rounded px-1 py-1"
                        >
                          {["jne", "jnt", "pos", "sicepat", "anteraja", "gojek", "grab", "ninja", "lion", "dhl", "fedex"].map((c) => <option key={c}>{c}</option>)}
                        </select>
                        <button onClick={() => saveWaybill(o.id)} className="text-xs px-2 py-1 bg-holy-red text-ivory rounded">+</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {orders.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No orders yet</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setDetail(null)} data-testid="order-detail-overlay">
          <div className="bg-ivory w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-serif text-2xl text-holy-red" data-testid="order-detail-number">{detail.order_number}</h3>
                <div className="text-xs text-muted-foreground">{new Date(detail.created_at).toLocaleString("id-ID")}</div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="order-detail-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-xl border border-border/60 p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Customer</div>
                <div className="text-sm font-semibold" data-testid="order-detail-customer">{detail.customer_name}</div>
                <div className="text-xs">{detail.customer_email}</div>
                <div className="text-xs">{detail.customer_phone}</div>
                {getWaLink(detail) && (
                  <a
                    href={getWaLink(detail)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="order-detail-wa-btn"
                    className="mt-3 inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold rounded-full px-3.5 py-1.5 hover:bg-[#1da851] transition-all shadow-sm"
                  >
                    <Phone className="w-3.5 h-3.5" /> 💬 Follow-up via WhatsApp
                  </a>
                )}
              </div>
              <div className="bg-white rounded-xl border border-border/60 p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Alamat Kirim</div>
                <div className="text-xs leading-relaxed">
                  {detail.address}<br />
                  {detail.city}{detail.province ? `, ${detail.province}` : ""} {detail.postal_code}<br />
                  {detail.country}
                </div>
                {detail.notes && <div className="text-xs mt-2 pt-2 border-t border-border/40 text-muted-foreground">📝 {detail.notes}</div>}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-border/60 p-4 mb-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Item Pesanan</div>
              <table className="w-full text-xs" data-testid="order-detail-items">
                <tbody>
                  {(detail.items || []).map((it, i) => (
                    <tr key={i} className="border-b border-border/30 last:border-0">
                      <td className="py-2 pr-2">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-muted-foreground">{it.quantity} × Rp{(it.price_idr || 0).toLocaleString("id-ID")}</div>
                      </td>
                      <td className="py-2 text-right font-semibold whitespace-nowrap">
                        Rp{((it.price_idr || 0) * it.quantity).toLocaleString("id-ID")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-xs mt-3 pt-3 border-t border-border/50 space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>Rp{(detail.subtotal_idr || 0).toLocaleString("id-ID")}</span></div>
                {detail.discount_idr > 0 && (
                  <div className="flex justify-between text-holy-green"><span>Diskon{detail.promo_code ? ` (${detail.promo_code})` : ""}</span><span>-Rp{detail.discount_idr.toLocaleString("id-ID")}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Ongkir ({String(detail.shipping_courier || "").toUpperCase()} {detail.shipping_service})</span><span>Rp{(detail.shipping_cost_idr || 0).toLocaleString("id-ID")}</span></div>
                <div className="flex justify-between text-base font-serif font-semibold text-holy-red pt-1 border-t border-border/40"><span>Total</span><span data-testid="order-detail-total">Rp{(detail.total_idr || 0).toLocaleString("id-ID")}</span></div>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 text-xs">
              <div className="bg-white rounded-xl border border-border/60 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Pembayaran</div>
                <div className="font-semibold uppercase">{detail.payment_method || "-"}</div>
                <div className={`inline-block mt-1 px-2 py-0.5 rounded-full ${detail.payment_status === "paid" ? "bg-holy-green/15 text-holy-green" : "bg-holy-gold/15 text-holy-gold-dark"}`}>{detail.payment_status}</div>
                {detail.paid_at && <div className="text-muted-foreground mt-1">Dibayar: {new Date(detail.paid_at).toLocaleString("id-ID")}</div>}
              </div>
              <div className="bg-white rounded-xl border border-border/60 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Status Order</div>
                <div className="font-semibold capitalize">{detail.status}</div>
              </div>
              <div className="bg-white rounded-xl border border-border/60 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Resi / Waybill</div>
                <div className="font-mono">{detail.waybill_id || "Belum diisi"}</div>
                {detail.shipping_courier_code && <div className="text-muted-foreground uppercase">{detail.shipping_courier_code}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsTab = () => {
  const { setSettings: setGlobal } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div>Loading…</div>;
  const save = async () => {
    const { data } = await api.put("/settings", s);
    setGlobal(data);
    alert("Settings saved");
  };
  return (
    <div>
      <h2 className="font-serif text-2xl mb-4">Site Settings</h2>
      <div className="bg-white rounded-2xl border border-border/60 p-6 grid sm:grid-cols-2 gap-3">
        <Field label="Site Name" value={s.site_name} onChange={(e) => setS({ ...s, site_name: e.target.value })} />
        <div className="sm:col-span-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Logo</span>
          <div className="mt-1">
            <ImageUploader
              value={s.logo_url}
              onChange={(url) => setS({ ...s, logo_url: url })}
              testid="logo-upload"
            />
          </div>
        </div>
        <Field label="Tagline (ID)" value={s.tagline_id} onChange={(e) => setS({ ...s, tagline_id: e.target.value })} />
        <Field label="Tagline (EN)" value={s.tagline_en} onChange={(e) => setS({ ...s, tagline_en: e.target.value })} />
        <Field label="Primary Color" value={s.primary_color} onChange={(e) => setS({ ...s, primary_color: e.target.value })} />
        <Field label="Secondary Color" value={s.secondary_color} onChange={(e) => setS({ ...s, secondary_color: e.target.value })} />
        <Field label="Accent Color" value={s.accent_color} onChange={(e) => setS({ ...s, accent_color: e.target.value })} />
        <Field label="Instagram" value={s.instagram} onChange={(e) => setS({ ...s, instagram: e.target.value })} />
        <Field label="WhatsApp" value={s.whatsapp} onChange={(e) => setS({ ...s, whatsapp: e.target.value })} />
        <Field label="Email" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} />
        <TextArea label="About (ID)" value={s.about_id} onChange={(e) => setS({ ...s, about_id: e.target.value })} />
        <TextArea label="About (EN)" value={s.about_en} onChange={(e) => setS({ ...s, about_en: e.target.value })} />
        <TextArea
          label="Footer Text (ID) — kalimat pendek di footer"
          value={s.footer_text_id || ""}
          onChange={(e) => setS({ ...s, footer_text_id: e.target.value })}
        />
        <TextArea
          label="Footer Text (EN) — short footer caption"
          value={s.footer_text_en || ""}
          onChange={(e) => setS({ ...s, footer_text_en: e.target.value })}
        />
        <TextArea
          label="Jam Operasional (ID)"
          value={s.opening_hours_id || ""}
          onChange={(e) => setS({ ...s, opening_hours_id: e.target.value })}
        />
        <TextArea
          label="Opening Hours (EN)"
          value={s.opening_hours_en || ""}
          onChange={(e) => setS({ ...s, opening_hours_en: e.target.value })}
        />
      </div>
      <button data-testid="save-settings-btn" onClick={save} className="mt-4 bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm">Save Settings</button>
    </div>
  );
};

const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/promos").then((r) => setPromos(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...editing,
      code: editing.code.trim().toUpperCase(),
      discount_value: +editing.discount_value,
      min_subtotal_idr: +editing.min_subtotal_idr,
      max_uses: +editing.max_uses,
    };
    try {
      if (editing.id) await api.put(`/promos/${editing.id}`, payload);
      else await api.post("/promos", payload);
      setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete promo?")) return;
    await api.delete(`/promos/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl">Promo Codes</h2>
        <button data-testid="add-promo-btn" onClick={() => setEditing({ code: "", discount_type: "percent", discount_value: 10, min_subtotal_idr: 0, max_uses: 0, active: true, label_id: "", label_en: "" })} className="inline-flex items-center gap-2 bg-holy-red text-ivory rounded-full px-5 py-2 text-sm">
          <Plus className="w-4 h-4" /> New Code
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-border/60 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-cream text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Discount</th><th className="text-left px-4 py-3">Min Order</th><th className="text-left px-4 py-3">Uses</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.id} className="border-t border-border/40">
                <td className="px-4 py-3 font-mono font-semibold">{p.code}</td>
                <td className="px-4 py-3">{p.discount_type === "percent" ? `${p.discount_value}%` : `Rp${(+p.discount_value).toLocaleString("id-ID")}`}</td>
                <td className="px-4 py-3 text-xs">Rp{(+p.min_subtotal_idr).toLocaleString("id-ID")}</td>
                <td className="px-4 py-3 text-xs">{p.uses || 0}{p.max_uses ? `/${p.max_uses}` : ""}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${p.active ? "bg-holy-green/15 text-holy-green" : "bg-foreground/10 text-muted-foreground"}`}>{p.active ? "Active" : "Off"}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...p })} className="text-holy-red mr-2" data-testid={`edit-promo-${p.code}`}><Edit3 className="inline w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-holy-red"><Trash2 className="inline w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {promos.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No promo codes yet</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-ivory rounded-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl text-holy-red">{editing.id ? "Edit" : "New"} Promo</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Code (uppercase)" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} />
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Discount Type</span>
                <select value={editing.discount_type} onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })} className="mt-1 w-full bg-white border border-border rounded-lg px-3 py-2 text-sm">
                  <option value="percent">Percent (%)</option>
                  <option value="fixed">Fixed (Rp)</option>
                </select>
              </label>
              <Field label="Discount Value" type="number" value={editing.discount_value} onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} />
              <Field label="Min Subtotal (IDR)" type="number" value={editing.min_subtotal_idr} onChange={(e) => setEditing({ ...editing, min_subtotal_idr: e.target.value })} />
              <Field label="Max Uses (0=unlimited)" type="number" value={editing.max_uses} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} />
              <Field label="Label (ID)" value={editing.label_id} onChange={(e) => setEditing({ ...editing, label_id: e.target.value })} />
              <Field label="Label (EN)" value={editing.label_en} onChange={(e) => setEditing({ ...editing, label_en: e.target.value })} />
              <label className="flex items-center gap-2 mt-5">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-holy-red" />
                <span className="text-sm">Active</span>
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button data-testid="save-promo-btn" onClick={save} className="bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm">Save</button>
              <button onClick={() => setEditing(null)} className="border border-border rounded-full px-6 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PasswordTab = () => {
  const [cur, setCur] = useState("");
  const [neu, setNeu] = useState("");
  const [conf, setConf] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (neu !== conf) { setErr("Password baru dan konfirmasi tidak cocok"); return; }
    setLoading(true);
    try {
      await api.put("/auth/password", { current_password: cur, new_password: neu });
      setMsg("Password berhasil diubah");
      setCur(""); setNeu(""); setConf("");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  return (
    <div>
      <h2 className="font-serif text-2xl mb-4">Change Password</h2>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-border/60 p-6 max-w-md space-y-3">
        <Field label="Current Password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required />
        <Field label="New Password (min 8 chars)" type="password" value={neu} onChange={(e) => setNeu(e.target.value)} required />
        <Field label="Confirm New Password" type="password" value={conf} onChange={(e) => setConf(e.target.value)} required />
        {msg && <div className="text-sm text-holy-green" data-testid="password-success">✓ {msg}</div>}
        {err && <div className="text-sm text-holy-red" data-testid="password-error">{err}</div>}
        <button data-testid="change-password-btn" disabled={loading} className="bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm disabled:opacity-50">
          {loading ? "..." : "Update Password"}
        </button>
      </form>
    </div>
  );
};

const CategoriesTab = () => {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/categories/all").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const payload = {
      ...editing,
      order: +editing.order,
      slug: editing.slug.trim().toLowerCase(),
      parent_slug: editing.parent_slug || null,
      image_url: editing.image_url || "",
    };
    try {
      if (editing.id) await api.put(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      setEditing(null);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
  };
  const parentOptions = cats.filter((c) => !c.parent_slug && (!editing?.id || c.id !== editing.id));
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl">Categories</h2>
        <button data-testid="add-category-btn" onClick={() => setEditing({ slug: "", name_id: "", name_en: "", icon: "✨", image_url: "", order: cats.length, active: true, parent_slug: "" })} className="inline-flex items-center gap-2 bg-holy-red text-ivory rounded-full px-5 py-2 text-sm">
          <Plus className="w-4 h-4" /> New Category
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-border/60 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-cream text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Icon</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Name (ID)</th><th className="text-left px-4 py-3">Parent</th><th className="text-left px-4 py-3">Order</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} className="border-t border-border/40">
                <td className="px-4 py-3">
                  {c.image_url ? (
                    <img src={c.image_url} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-border" />
                  ) : (
                    <span className="text-xl">{c.icon}</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{c.parent_slug && <span className="text-muted-foreground">↳ </span>}{c.slug}</td>
                <td className="px-4 py-3">{c.name_id}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{c.parent_slug || "—"}</td>
                <td className="px-4 py-3">{c.order}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${c.active ? "bg-holy-green/15 text-holy-green" : "bg-foreground/10 text-muted-foreground"}`}>{c.active ? "Active" : "Hidden"}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...c, parent_slug: c.parent_slug || "" })} className="text-holy-red mr-2" data-testid={`edit-cat-${c.slug}`}><Edit3 className="inline w-4 h-4" /></button>
                  <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/categories/${c.id}`); load(); } }} className="text-muted-foreground hover:text-holy-red"><Trash2 className="inline w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {cats.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No categories yet</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-ivory rounded-2xl w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h3 className="font-serif text-2xl text-holy-red">{editing.id ? "Edit" : "New"} Category</h3><button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Slug (lowercase, no spaces)" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              <Field label="Icon (emoji) — fallback" value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} />
              <Field label="Name (ID)" value={editing.name_id} onChange={(e) => setEditing({ ...editing, name_id: e.target.value })} />
              <Field label="Name (EN)" value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} />
              <Field label="Order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Parent Category</label>
                <select
                  data-testid="category-parent-select"
                  value={editing.parent_slug || ""}
                  onChange={(e) => setEditing({ ...editing, parent_slug: e.target.value })}
                  className="w-full bg-ivory border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-holy-red"
                >
                  <option value="">— None (Top Level) —</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.slug}>{p.icon} {p.name_id} ({p.slug})</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Logo / Foto Kategori (opsional — pakai foto Anda sendiri)</label>
                <ImageUploader
                  value={editing.image_url || ""}
                  onChange={(url) => setEditing({ ...editing, image_url: url })}
                  testid={`category-image-upload-${editing.slug || "new"}`}
                />
                <p className="text-[11px] text-muted-foreground mt-1">Jika foto diisi, foto akan dipakai sebagai logo. Jika kosong, emoji di atas yang dipakai. Rasio terbaik 1:1 (persegi).</p>
              </div>
              <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-holy-red" /><span className="text-sm">Active</span></label>
            </div>
            <div className="flex gap-2 mt-6">
              <button data-testid="save-category-btn" onClick={save} className="bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm">Save</button>
              <button onClick={() => setEditing(null)} className="border border-border rounded-full px-6 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MenusTab = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings({ ...r.data, nav_links: r.data.nav_links || [], footer_links: r.data.footer_links || [] })); }, []);
  if (!settings) return <div>Loading...</div>;

  const addLink = (key) => setSettings({ ...settings, [key]: [...settings[key], { label_id: "", label_en: "", url: "/", order: settings[key].length, active: true }] });
  const removeLink = (key, i) => setSettings({ ...settings, [key]: settings[key].filter((_, idx) => idx !== i) });
  const updateLink = (key, i, field, val) => {
    const next = [...settings[key]];
    next[i] = { ...next[i], [field]: val };
    setSettings({ ...settings, [key]: next });
  };

  const save = async () => {
    await api.put("/settings", settings);
    alert("Menu saved! Refresh halaman untuk lihat perubahan.");
  };

  const renderSection = (title, keyName) => (
    <div className="bg-white rounded-2xl border border-border/60 p-5 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-serif text-lg">{title}</h3>
        <button onClick={() => addLink(keyName)} className="text-xs text-holy-red hover:underline" data-testid={`add-${keyName}`}>+ Tambah Link</button>
      </div>
      <div className="space-y-2">
        {settings[keyName].map((l, i) => (
          <div key={i} className="bg-cream rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_80px_60px_auto] gap-2 items-center">
            <input value={l.label_id} onChange={(e) => updateLink(keyName, i, "label_id", e.target.value)} placeholder="Label (ID)" className="bg-white border border-border rounded-lg px-3 py-2 text-sm" />
            <input value={l.label_en} onChange={(e) => updateLink(keyName, i, "label_en", e.target.value)} placeholder="Label (EN)" className="bg-white border border-border rounded-lg px-3 py-2 text-sm" />
            <input value={l.url} onChange={(e) => updateLink(keyName, i, "url", e.target.value)} placeholder="/path" className="bg-white border border-border rounded-lg px-3 py-2 text-sm" />
            <input type="number" value={l.order} onChange={(e) => updateLink(keyName, i, "order", +e.target.value)} placeholder="Order" className="bg-white border border-border rounded-lg px-2 py-2 text-sm" />
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={l.active} onChange={(e) => updateLink(keyName, i, "active", e.target.checked)} className="accent-holy-red" /> Aktif</label>
            <button onClick={() => removeLink(keyName, i)} className="text-xs text-holy-red hover:underline">×</button>
          </div>
        ))}
        {settings[keyName].length === 0 && <div className="text-xs text-muted-foreground text-center py-3">No links yet. Klik &quot;Tambah Link&quot; untuk menambah.</div>}
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="font-serif text-2xl mb-4">Menu Links</h2>
      <p className="text-sm text-muted-foreground mb-4">Tambah, edit, atau hapus link di header dan footer. Kosongkan = pakai default Holy Flowy.</p>
      {renderSection("Header Navigation", "nav_links")}
      {renderSection("Footer Links", "footer_links")}
      <button data-testid="save-menus-btn" onClick={save} className="bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm">Save Menu</button>
    </div>
  );
};

export default AdminDashboard;
